/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Selectors for table
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Mikhail Kokoulin
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import get from 'lodash/get';
import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../../reducers/rootReducer';
import { initialTableState, ITableBasketParams, ITableReducer } from '../reducers/tableReducer';
import { IChangeTableModalValuePayload, TableNameType } from '../types/tableTypes';

export const selectTable = (state: IRootReducer): ITableReducer => get(state, 'tableReducer', initialTableState);

/*
* -------------------------------------------
*              FAST ACTIONS
* -------------------------------------------
*/

export const makeSelectorBasketContext = (): 
    OutputSelector<IRootReducer, string | undefined, 
    (state: ITableReducer) => string | undefined> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state.basketContext,
    );

export const makeSelectorTableFastActionAllIn =
    (tableName?: TableNameType): OutputSelector<IRootReducer, boolean, (state: ITableReducer) => boolean> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].fastActionAllIn ?? false,
        );

export const makeSelectorIsFastActionPage = (): 
    OutputSelector<IRootReducer, boolean | undefined, 
    (state: ITableReducer) => boolean | undefined> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state.isFastActionPage,
    );

export const makeSelectorIsFastBasketPage = (): 
    OutputSelector<IRootReducer, boolean | undefined, 
    (state: ITableReducer) => boolean | undefined> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state.isFastBasketPage,
    );

/*
* -------------------------------------------
*              TABLE INFO
* -------------------------------------------
*/

export const makeSelectorMobileCashFileIsLoading = (tableName?: TableNameType): 
    OutputSelector<IRootReducer, boolean | undefined,
    (state: ITableReducer) => boolean | undefined> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state[tableName || state.currentTableName].isMobileCashFileLoading,
    );

export const makeSelectorMobileCashFileIsSuccessful = (tableName?: TableNameType): 
    OutputSelector<IRootReducer, boolean | undefined,
    (state: ITableReducer) => boolean | undefined> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state[tableName || state.currentTableName].isMobileCashFileSuccessful,
    );

export const makeSelectorTableCurrentName = (): OutputSelector<IRootReducer,
    TableNameType,
    (state: ITableReducer) => TableNameType> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state.currentTableName,
    );

export const makeSelectorTableIsLoaded = (tableName?: TableNameType): OutputSelector<IRootReducer,
    boolean,
    (state: ITableReducer) => boolean> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state[tableName || state.currentTableName].isLoaded,
    );

export const makeSelectorTableIsLoading = (tableName?: TableNameType): OutputSelector<IRootReducer,
    boolean,
    (state: ITableReducer) => boolean> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state[tableName || state.currentTableName].isLoading,
    );

export const makeSelectorTableParameters = (tableName?: TableNameType): OutputSelector<IRootReducer,
    any,
    (state: ITableReducer) => any> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state[tableName || state.currentTableName].parameters,
    );

/*
* -------------------------------------------
*              TABLE DATA
* -------------------------------------------
*/

export const makeSelectorTableData =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['data'],
        (state: ITableReducer) => ITableReducer[TableNameType]['data']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].data,
        );

export const makeSelectorTableFooter =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['tableFooter'],
        (state: ITableReducer) => ITableReducer[TableNameType]['tableFooter']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].tableFooter,
        );

export const makeSelectorTableMessage =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['message'],
        (state: ITableReducer) => ITableReducer[TableNameType]['message']> =>
        createSelector(
            selectTable,
            (state) => state[tableName || state.currentTableName].message,
        );

export const makeSelectorTableSearchAfterValues =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['searchAfterValues'],
        (state: ITableReducer) => ITableReducer[TableNameType]['searchAfterValues']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].searchAfterValues,
        );

/*
* -------------------------------------------
*              TABLE SUB ROWS
* -------------------------------------------
*/

export const makeSelectorTableSubRowsIds =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['subRowsIds'],
        (state: ITableReducer) => ITableReducer[TableNameType]['subRowsIds']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].subRowsIds,
        );

export const makeSelectorCollapseTableSubRows =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['collapseTableSubRows'],
        (state: ITableReducer) => ITableReducer[TableNameType]['collapseTableSubRows']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].collapseTableSubRows,
        );

/*
* -------------------------------------------
*              TABLE FIELDS
* -------------------------------------------
*/
export const makeSelectorTableFields =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['fields'],
        (state: ITableReducer) => ITableReducer[TableNameType]['fields']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].fields,
        );

/*
* -------------------------------------------
*         TABLE FILTER & SORT
* -------------------------------------------
*/

export const makeSelectorTableFilterOptions =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['filterOptions'],
        (state: ITableReducer) => ITableReducer[TableNameType]['filterOptions']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].filterOptions,
        );

export const makeSelectorTableFilter =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['filter'],
        (state: ITableReducer) => ITableReducer[TableNameType]['filter']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].filter,
        );

export const makeSelectorTableIsFilterSelected =
    (tableName?: TableNameType): OutputSelector<IRootReducer, boolean, (state: ITableReducer) => boolean> =>
        createSelector(selectTable, (state: ITableReducer) =>
            Object.keys(state[tableName || state.currentTableName].filter).length !== 0,
        );

export const makeSelectorTableSort =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['sort'],
        (state: ITableReducer) => ITableReducer[TableNameType]['sort']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].sort,
        );

/*
* -------------------------------------------
*              TABLE CHECKBOX
* -------------------------------------------
*/

export const makeSelectorTableBasketAllIn =
    (tableName?: TableNameType): OutputSelector<IRootReducer, boolean, (state: ITableReducer) => boolean> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].basketAllIn,
        );

export const makeSelectorTableBasketCount =
    (tableName?: TableNameType): OutputSelector<IRootReducer, number | null,
        (state: ITableReducer) => number | null> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => get(state[tableName || state.currentTableName], 'basketCount', 0),
        );

export const makeSelectorTableBasketValid =
    (tableName?: TableNameType): OutputSelector<IRootReducer, boolean, (state: ITableReducer) => boolean> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].basketValid,
        );

export const makeSelectorTableBasketParams = (tableName?: TableNameType):
    OutputSelector<IRootReducer, ITableBasketParams | undefined,
        (state: ITableReducer) => ITableBasketParams | undefined> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state[tableName || state.currentTableName].basketParams,
    );

/*
* -------------------------------------------
*                SEND SEARCH
* -------------------------------------------
*/

export const makeSelectorTableFilterSearchResult = (tableName?: TableNameType): OutputSelector<IRootReducer,
    string[],
    (state: ITableReducer) => string[]> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state[tableName || state.currentTableName].filterSearch.searchResult,
    );

export const makeSelectorTableFilterSearchValue = (tableName?: TableNameType): OutputSelector<IRootReducer,
    string,
    (state: ITableReducer) => string> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state[tableName || state.currentTableName].filterSearch.searchValue,
    );
/*
* -------------------------------------------
*                SUGGEST FIELD
* -------------------------------------------
*/
export const makeSelectorTableMetaFacets = (
    tableName?: TableNameType,
): OutputSelector<IRootReducer,
    ITableReducer[TableNameType]['metaFacets'],
    (state: ITableReducer) => ITableReducer[TableNameType]['metaFacets']> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state[tableName || state.currentTableName].metaFacets,
    );

export const makeSelectorTableSuggestIsLoading = (tableName?: TableNameType): OutputSelector<IRootReducer,
    boolean,
    (state: ITableReducer) => boolean> =>
    createSelector(
        selectTable,
        (state: ITableReducer) => state[tableName || state.currentTableName].suggestLoading,
    );
/*
* -------------------------------------------
*              EXPORT
* -------------------------------------------
*/

export const makeSelectorTableExportExtensions =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['exportExtensions'],
        (state: ITableReducer) => ITableReducer[TableNameType]['exportExtensions']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].exportExtensions,
        );

/*
* -------------------------------------------
*              TABLE MODAL
* -------------------------------------------
*/
export const makeSelectorTableModalValue =
    (): OutputSelector<IRootReducer,
        IChangeTableModalValuePayload,
        (state: ITableReducer) => IChangeTableModalValuePayload> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state.modalProps,
        );

export const makeSelectorTableCount =
    (tableName?: TableNameType): OutputSelector<IRootReducer, number, (state: ITableReducer) => number> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].count,
        );

/*
* -------------------------------------------
*         TABLE FOOTER BUTTONS
* -------------------------------------------
*/
export const makeSelectorTableButtons =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['buttons'],
        (state: ITableReducer) => ITableReducer[TableNameType]['buttons']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => get(state[tableName || state.currentTableName], 'buttons', []),
        );
/*
* -------------------------------------------
*         TABLE SIZE
* -------------------------------------------
*/
export const makeSelectorTablePage =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['page'],
        (state: ITableReducer) => ITableReducer[TableNameType]['page']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => get(state[tableName || state.currentTableName], 'page', 0),
        );

/*
* -------------------------------------------
*        BUTTONS MODE
* -------------------------------------------
*/

export const makeSelectorTableButtonsMode =
    (tableName?: TableNameType): OutputSelector<IRootReducer, ITableReducer[TableNameType]['buttonsMode'],
        (state: ITableReducer) => ITableReducer[TableNameType]['buttonsMode']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => get(state[tableName || state.currentTableName], 'buttonsMode'),
        );

export const makeSelectorTableButtonsForCheckOnMode =
    (tableName?: TableNameType): OutputSelector<IRootReducer, ITableReducer[TableNameType]['buttonsForCheckOnMode'],
        (state: ITableReducer) => ITableReducer[TableNameType]['buttonsForCheckOnMode']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => get(state[tableName || state.currentTableName], 'buttonsForCheckOnMode'),
        );

/*
* -------------------------------------------
*             REMOVED ROW INDEXES
* -------------------------------------------
*/

export const makeSelectorTableRemovedRowIndexes =
    (tableName?: TableNameType): OutputSelector<IRootReducer,
        ITableReducer[TableNameType]['removedRowIndexes'],
        (state: ITableReducer) => ITableReducer[TableNameType]['removedRowIndexes']> =>
        createSelector(
            selectTable,
            (state: ITableReducer) => state[tableName || state.currentTableName].removedRowIndexes,
        );
